import { gsap } from 'gsap';
import { resolveElement } from './dom';
import { getDomain, phoneToHref } from './string';
import { initYmaps3 } from './ymaps3';
import ymapScheme from './ymapScheme.json';

const DEFAULT_CONTROLS = {
  zoomIn: '.map__zoom-in',
  zoomOut: '.map__zoom-out',
};

function popupTemplate(place) {
  const title = place?.title || place?.name || '';

  const addresses = Array.isArray(place.addresses) ? place.addresses : [];
  const phones = Array.isArray(place.phones) ? place.phones : [];
  const instagrams = Array.isArray(place.instagrams) ? place.instagrams : [];
  const websites = Array.isArray(place.websites) ? place.websites : [];

  let html = '<div class="map__popup">';

  if (place.logo) {
    html += '<picture class="map__logo">';
    html += `<img src="${place.logo}" loading="lazy" alt="">`;
    html += '</picture>';
  }

  html += '<div class="map__info">';
  html += `<p class="map__title">${title}</p>`;

  addresses.forEach((addr) => {
    html += `<p class="map__contact">${addr}</p>`;
  });

  phones.forEach((phone) => {
    html += `<a href="${phoneToHref(phone)}" class="map__contact">${phone}</a>`;
  });

  instagrams.forEach((ig) => {
    const handle = String(ig).replace(/^@/, '');
    html += `<a href="https://www.instagram.com/${handle}/" class="map__contact" target="_blank" rel="nofollow noopener noreferrer">@${handle}</a>`;
  });

  websites.forEach((url) => {
    html += `<a href="${url}" class="map__contact" target="_blank" rel="nofollow noopener noreferrer">${getDomain(url)}</a>`;
  });

  html += `
    </div>
  </div>`;
  return html;
}

// Compute bounds from an array of [lng, lat]
function computeBounds(coordsArray) {
  const [lng0, lat0] = coordsArray[0];
  let minLng = lng0,
    maxLng = lng0,
    minLat = lat0,
    maxLat = lat0;
  for (const [lng, lat] of coordsArray) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

// Pad bounds by a fraction of their span (uniform)
function padBounds(bounds, fraction = 0.1) {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  const spanLng = Math.max(1e-9, maxLng - minLng);
  const spanLat = Math.max(1e-9, maxLat - minLat);
  const padLng = spanLng * fraction;
  const padLat = spanLat * fraction;
  return [
    [minLng - padLng, minLat - padLat],
    [maxLng + padLng, maxLat + padLat],
  ];
}

// Pad bounds by per-side fractions: { top, right, bottom, left } or number (uniform)
function padBoundsBySides(bounds, padding = 0) {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  const spanLng = Math.max(1e-9, maxLng - minLng);
  const spanLat = Math.max(1e-9, maxLat - minLat);

  const sides =
    typeof padding === 'number'
      ? { top: padding, right: padding, bottom: padding, left: padding }
      : padding || {};

  const { top = 0, right = 0, bottom = 0, left = 0 } = sides;

  const newMinLng = minLng - left * spanLng;
  const newMaxLng = maxLng + right * spanLng;
  const newMinLat = minLat - bottom * spanLat; // south
  const newMaxLat = maxLat + top * spanLat; // north

  return [
    [newMinLng, newMinLat],
    [newMaxLng, newMaxLat],
  ];
}

// Apply location update using either setLocation or update, depending on the API available
function applyLocation(map, location, duration) {
  if (!map) return;
  const loc = { ...location };
  if (typeof duration === 'number') loc.duration = duration;

  if (typeof map.setLocation === 'function') {
    map.setLocation(loc);
  } else if (typeof map.update === 'function') {
    map.update({ location: loc });
  }
}

// Fit map to all markers; if only one marker, center and set a zoom
// Options:
// - padding: { top, right, bottom, left } fractions of bounds span (preferred)
// - paddingFraction: number (legacy uniform padding)
// - singleZoom: number
// - animateMs: number
function fitToMarkers(map, coords, options = {}) {
  const {
    padding, // per-side fractions
    paddingFraction, // legacy uniform
    singleZoom = 15,
    animateMs = 300,
  } = options;

  if (!coords || coords.length === 0 || !map) return;

  if (coords.length === 1) {
    applyLocation(map, { center: coords[0], zoom: singleZoom }, animateMs);
    return;
  }

  const rawBounds = computeBounds(coords);
  const paddedBounds =
    padding != null
      ? padBoundsBySides(rawBounds, padding)
      : padBounds(rawBounds, paddingFraction ?? 0.1);

  applyLocation(map, { bounds: paddedBounds }, animateMs);
}

// Helper to convert places to GeoJSON-like features for clusterer
function placesToFeatures(places) {
  return places.map((place, idx) => {
    const coordinates = [place.location.lng, place.location.lat];
    return {
      type: 'Feature',
      id: place.id ?? idx,
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        place,
      },
    };
  });
}

// Simple DOM for cluster icon
function clusterCircle(count) {
  const circle = document.createElement('div');
  circle.classList.add('map__marker', 'map__marker--clusterer');
  circle.innerHTML = `<span class="map__marker-text">${count}</span>`;
  return circle;
}

// Create marker DOM (wrap, button, popup DOM string inserted)
function createMarkerDom(place) {
  const markerWrap = document.createElement('div');
  markerWrap.className = 'map__marker-wrap';

  const markerButton = document.createElement('button');
  markerButton.setAttribute('type', 'button');
  markerButton.className = 'map__marker';
  markerButton.innerHTML = `
    <svg class="map__marker-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M12 9L6 9" stroke="#fff" stroke-width="1.6" />
      <path class="plus" d="M9 6V12" stroke="#fff" stroke-width="1.6" />
    </svg>`;
  markerWrap.appendChild(markerButton);

  if (place) {
    markerButton.insertAdjacentHTML('afterend', popupTemplate(place));
  }
  const popup = markerButton.nextElementSibling;

  return { markerWrap, markerButton, popup };
}

export class ClinicsYandexMap {
  constructor(mapElement, options = {}) {
    this.mapElement = mapElement;

    this.options = {
      apiKey: options.apiKey,

      controls: { ...DEFAULT_CONTROLS, ...(options.controls || {}) },
      behaviors: options.behaviors || [
        'drag',
        /* 'scrollZoom', */ 'pinchZoom',
        'dblClick',
      ],
      mode: options.mode || 'vector',
      defaultLocation: options.defaultLocation || {
        center: [54.8, 24.5],
        zoom: 9,
      },
      ymapScheme: options.ymapScheme || ymapScheme,

      // Responsive per-side fit padding (fractions of bounds span)
      fitPadding: options.fitPadding || {
        mobile: { top: 0.08, right: 0.08, bottom: 0.3, left: 0.08 },
        desktop: { top: 0.08, right: 0.08, bottom: 0.18, left: 0.08 },
      },
      fitPaddingBreakpoint: options.fitPaddingBreakpoint ?? 1024,
      singleMarkerZoom: options.singleMarkerZoom ?? 14,
      fitAnimateMs: options.fitAnimateMs ?? 300,

      // Clusterer options
      cluster: {
        enabled: options.cluster?.enabled ?? true,
        gridSize: options.cluster?.gridSize ?? 64,
      },
    };

    this.map = null;
    this.mm = null;
    this.zoomInBtn = null;
    this.zoomOutBtn = null;

    // Plain markers bookkeeping
    // { marker, markerWrap, markerButton, popup, onMarkerClick, handleOutsideClick }
    this._markers = [];

    // Clusterer bookkeeping
    this._clusterer = null;
    this._clusterPkg = null; // { YMapClusterer, clusterByGrid }
    this._clusterDisposers = []; // functions to remove doc listeners created by cluster marker renderers

    this._bound = {
      onZoomIn: null,
      onZoomOut: null,
      onCityChange: null,
    };
  }

  async init() {
    await initYmaps3(this.options.apiKey);

    const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer } = ymaps3;

    this.map = new YMap(this.mapElement, {
      location: this.options.defaultLocation,
      behaviors: this.options.behaviors,
      mode: this.options.mode,
    });

    this.map.addChild(
      new YMapDefaultSchemeLayer({ customization: this.options.ymapScheme }),
    );
    this.map.addChild(new YMapDefaultFeaturesLayer());

    this._bindControls();

    return this;
  }

  clearPlaces() {
    // Remove document listeners and marker click listeners for plain markers
    for (const m of this._markers) {
      if (m.handleOutsideClick) {
        document.removeEventListener('mousedown', m.handleOutsideClick);
      }
      if (m.markerButton && m.onMarkerClick) {
        m.markerButton.removeEventListener('click', m.onMarkerClick);
      }
      if (this.map && m.marker) {
        this.map.removeChild(m.marker);
      }
    }
    this._markers = [];
  }

  async showPlaces(places = []) {
    const { YMapMarker } = ymaps3;

    // Clear plain markers; clusterer manages its own
    this.clearPlaces();

    const markerCoords = [];
    const coordsFromPlaces = places.map((p) => [
      p.location.lng,
      p.location.lat,
    ]);

    if (this.options.cluster.enabled) {
      const features = placesToFeatures(places);
      await this._drawCluster(features);

      // Clean up old clustered doc listeners
      this._cleanupClusterListeners();

      if (this._clusterer && !this._clusterer.parent) {
        this.map.addChild(this._clusterer);
      }

      markerCoords.push(...coordsFromPlaces);
    } else {
      // Render plain markers with custom DOM and popups
      for (const place of places) {
        const coordinates = [place.location.lng, place.location.lat];

        const { markerWrap, markerButton, popup } = createMarkerDom(place);

        const marker = new YMapMarker(
          {
            coordinates,
            draggable: false,
            mapFollowsOnDrag: false,
          },
          markerWrap,
        );
        this.map.addChild(marker);

        const onMarkerClick = () => {
          const isOpened = markerWrap.classList.contains('is-opened');
          if (!isOpened) {
            if (marker.element?.parentElement) {
              marker.element.parentElement.style.zIndex = 1;
            }
            markerWrap.classList.add('is-opened');
            popup && popup.classList.add('is-shown');
          } else {
            if (marker.element?.parentElement) {
              marker.element.parentElement.style.zIndex = 0;
            }
            markerWrap.classList.remove('is-opened');
            popup && popup.classList.remove('is-shown');
          }
        };
        markerButton.addEventListener('click', onMarkerClick);

        const handleOutsideClick = (event) => {
          if (
            popup &&
            !popup.contains(event.target) &&
            !markerButton.contains(event.target)
          ) {
            if (marker.element?.parentElement) {
              marker.element.parentElement.style.zIndex = 0;
            }
            markerWrap.classList.remove('is-opened');
            popup.classList.remove('is-shown');
          }
        };
        document.addEventListener('mousedown', handleOutsideClick);

        this._markers.push({
          marker,
          markerWrap,
          markerButton,
          popup,
          onMarkerClick,
          handleOutsideClick,
        });

        markerCoords.push(coordinates);
      }
    }

    // Fit view to all markers
    fitToMarkers(this.map, markerCoords, {
      padding: this._getFitPadding(),
      singleZoom: this.options.singleMarkerZoom,
      animateMs: this.options.fitAnimateMs,
    });
  }

  // Clusterer: create and wire up marker/cluster renderers
  async _drawCluster(features = []) {
    if (!this._clusterPkg) {
      this._clusterPkg = await import('@yandex/ymaps3-clusterer');
    }
    const { YMapClusterer, clusterByGrid } = this._clusterPkg;

    if (!this._clusterer) {
      // Marker renderer for clusterer: reuse custom DOM (button + popup)
      const markerRenderer = (feature) => {
        const coordinates = feature.geometry.coordinates;
        const place = feature.properties?.place;

        const { markerWrap, markerButton, popup } = createMarkerDom(place);

        const marker = new ymaps3.YMapMarker(
          {
            coordinates,
            draggable: false,
            mapFollowsOnDrag: false,
          },
          markerWrap,
        );

        const onMarkerClick = () => {
          const isOpened = markerWrap.classList.contains('is-opened');
          if (!isOpened) {
            if (marker.element?.parentElement) {
              marker.element.parentElement.style.zIndex = 1;
            }
            markerWrap.classList.add('is-opened');
            popup && popup.classList.add('is-shown');
          } else {
            if (marker.element?.parentElement) {
              marker.element.parentElement.style.zIndex = 0;
            }
            markerWrap.classList.remove('is-opened');
            popup && popup.classList.remove('is-shown');
          }
        };
        markerButton.addEventListener('click', onMarkerClick);

        const handleOutsideClick = (event) => {
          if (
            popup &&
            !popup.contains(event.target) &&
            !markerButton.contains(event.target)
          ) {
            if (marker.element?.parentElement) {
              marker.element.parentElement.style.zIndex = 0;
            }
            markerWrap.classList.remove('is-opened');
            popup.classList.remove('is-shown');
          }
        };
        document.addEventListener('mousedown', handleOutsideClick);

        // Track disposers to remove listeners when clusterer updates/detaches
        this._clusterDisposers.push(() => {
          try {
            markerButton.removeEventListener('click', onMarkerClick);
          } catch {}
          try {
            document.removeEventListener('mousedown', handleOutsideClick);
          } catch {}
        });

        return marker;
      };

      // Cluster renderer: show count; on click, fit to features bounds with padding
      const clusterRenderer = (coordinates, features) => {
        const dom = clusterCircle(features.length);

        const marker = new ymaps3.YMapMarker(
          {
            coordinates,
            onClick: () => {
              // Get current zoom and zoom in a bit towards this cluster
              const currentZoom = this._getZoom();
              const nextZoom = Math.min(currentZoom + 2, 21); // clamp to max zoom

              this._applyLocation(
                {
                  center: coordinates,
                  zoom: nextZoom,
                },
                this.options.fitAnimateMs,
              );
            },
          },
          dom,
        );

        return marker;
      };

      this._clusterer = new YMapClusterer({
        method: clusterByGrid({ gridSize: this.options.cluster.gridSize }),
        features,
        marker: markerRenderer,
        cluster: clusterRenderer,
      });
    }
  }

  _getFitPadding() {
    const isMobile = window.innerWidth < this.options.fitPaddingBreakpoint;
    const cfg = this.options.fitPadding || {};
    const mobile = cfg.mobile || {};
    const desktop = cfg.desktop || {};
    return isMobile ? mobile : desktop;
  }

  _bindControls() {
    this.zoomInBtn = resolveElement(this.options.controls.zoomIn);
    this.zoomOutBtn = resolveElement(this.options.controls.zoomOut);

    this._bound.onZoomIn = () => this._changeZoom(1);
    this._bound.onZoomOut = () => this._changeZoom(-1);

    if (this.zoomInBtn)
      this.zoomInBtn.addEventListener('click', this._bound.onZoomIn);
    if (this.zoomOutBtn)
      this.zoomOutBtn.addEventListener('click', this._bound.onZoomOut);
  }

  _getZoom() {
    if (!this.map) return this.options.defaultLocation?.zoom ?? 10;
    try {
      if (typeof this.map.getZoom === 'function') return this.map.getZoom();
      if (typeof this.map.getLocation === 'function') {
        const loc = this.map.getLocation();
        if (loc && typeof loc.zoom === 'number') return loc.zoom;
      }
      if (typeof this.map.zoom === 'number') return this.map.zoom;
    } catch {}
    return this.options.defaultLocation?.zoom ?? 10;
  }

  _applyLocation(location, duration = 300) {
    applyLocation(this.map, location, duration);
  }

  _changeZoom(delta) {
    const current = this._getZoom();
    const nextZoom = Math.max(2, Math.min(21, current + delta));
    this._applyLocation({ zoom: nextZoom }, 300);
  }

  _cleanupClusterListeners() {
    this._clusterDisposers.forEach((fn) => {
      try {
        fn();
      } catch {}
    });
    this._clusterDisposers = [];
  }
}
